import { useMemo } from "react";
import {
  type UseControllerProps,
  useForm,
  type UseFormReturn,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { useApi } from "@apac/hooks";
import {
  type AlertKey,
  type FlightKey,
  MessageMethodEnum,
  ShopFilter,
} from "@b2bportal/air-price-watch-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { emailRegex } from "@hopper-b2b/utilities";
import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  isWatchingSelector,
  passengerCountSelector,
  useCreateWatchMutation,
} from "../store";
import { useDeleteWatchTrip } from "./useDeleteWatchTrip";
import { useFetchFlightsForPriceWatch } from "./useFetchFlightsForPriceWatch";

type WatchTripResponse =
  | {
      data: unknown;
      error?: undefined;
    }
  | {
      data?: undefined;
      error: FetchBaseQueryError | SerializedError;
    };

export type PriceWatchForm = {
  email: string;
  nonstop: boolean;
  noLCC: boolean;
};

export type PriceWatchFormControls = Record<
  keyof PriceWatchForm,
  UseControllerProps<PriceWatchForm, keyof PriceWatchForm>
>;

export interface UseWatchTripFormParams {
  flightKey: AlertKey;
  email?: string;
}

interface UseWatchTripFormReturn {
  context: {
    form: UseFormReturn<PriceWatchForm, unknown, undefined>;
    formControls: PriceWatchFormControls;
    isWatching: boolean;
  };
  handlers: {
    unwatch: () => Promise<WatchTripResponse>;
    watch: () => Promise<WatchTripResponse>;
  };
}

export const useWatchTripForm = ({
  flightKey,
  email,
}: UseWatchTripFormParams): UseWatchTripFormReturn => {
  const { t } = useI18nContext();

  const formDefaultValues = useMemo(
    () => ({
      email,
      nonstop:
        flightKey.value.filter === ShopFilter.NonStop ||
        flightKey.value.filter === ShopFilter.NonStopNoLCC,
      noLCC:
        flightKey.value.filter === ShopFilter.NoLCC ||
        flightKey.value.filter === ShopFilter.NonStopNoLCC,
    }),
    [flightKey, email]
  );

  const form = useForm<PriceWatchForm>({
    mode: "onBlur",
    defaultValues: formDefaultValues,
  });

  const {
    handlers: { unwatch },
  } = useDeleteWatchTrip({ flightKey });

  const formControls: PriceWatchFormControls = {
    email: {
      name: "email",
      control: form.control,
      disabled: form.formState.isSubmitting,
      rules: {
        required: true,
        pattern: {
          value: emailRegex,
          message: t("priceWatchErrors.invalidEmail"),
        },
      },
    },
    nonstop: {
      name: "nonstop",
      control: form.control,
      disabled: form.formState.isSubmitting,
    },
    noLCC: {
      name: "noLCC",
      control: form.control,
      disabled: form.formState.isSubmitting,
    },
  };

  const passengers = useSelector(passengerCountSelector) as {
    [key: string]: number;
  };
  const [createWatch] = useCreateWatchMutation();
  const fetchFlightsForPriceWatch = useFetchFlightsForPriceWatch();
  const watchApi = useApi(
    (request: { flightKey: FlightKey; form: PriceWatchForm }) => {
      const watchRequest = {
        key: flightKey,
        method: {
          MessageMethod: MessageMethodEnum.Email,
          email: request.form.email,
        },
        passengers,
      };

      /*
       * TODO(fyoshimatsu): This is probably doing price-watch incorrectly.
       * We are calling createWatch _with_ the filters for the flightKey
       * that does _not_ have the filters. What we should do perhaps is
       * to search for flights with those shop filters first and have those
       * filters in the flightKey and then send it to price watch API.
       */
      if (request.form.noLCC || request.form.nonstop) {
        const shopFilter = request.form.noLCC
          ? request.form.nonstop
            ? ShopFilter.NonStopNoLCC
            : ShopFilter.NoLCC
          : request.form.nonstop
            ? ShopFilter.NonStop
            : ShopFilter.NoFilter;
        watchRequest["key"]["value"]["filter"] = shopFilter;
        return fetchFlightsForPriceWatch({
          flightKey,
          passengers,
          shopFilter,
        }).then(() => createWatch(watchRequest));
      }
      return createWatch(watchRequest);
    }
  );

  return {
    context: {
      form,
      formControls,
      isWatching: useSelector(isWatchingSelector),
    },
    handlers: {
      unwatch,
      watch: () => watchApi.trigger({ form: form.getValues(), flightKey }),
    },
  };
};
